// ------------------------------------------------------------------------------
// name: useReadyRequest for ahooks v3.x
// - 实现在 refreshDeeps 更新时，自动判断是否符合条件进行请求（以此规避掉不必要、不符合条件的请求）
// - 暂时不支持分页数据请求的场景，适用于较简单的全量数据请求，如有依赖条件的配置数据等
// - 替代原来基于 ahooks v2.x 的交易useDiscardRequest
// author: mudas
// created: 2024-08-20
// ------------------------------------------------------------------------------

import { useState } from 'react';
import { useAsyncEffect, useRequest } from 'ahooks';
import type { Options, Plugin, Result, Service } from 'ahooks/lib/useRequest/src/types';

type NewOptions<TData, TParams extends any[]> = Omit<Options<TData, TParams>, 'ready'> & {
  // 与 refreshDeps 一起实现在特定数据刷新后，达到指定条件才正式发出 Request 的目的，大大简化实现代码
  ready?: boolean | (() => boolean) | (() => Promise<boolean>);
};

/**
 * 条件请求
 *
 * @deprecated 不再推荐使用，请使用 useSafeQuery 替代（直接使用 enabled 参数）
 * @param service
 * @param options
 * @param plugins
 */
export const useReadyRequest = <TData, TParams extends any[]>(
  service: Service<TData, TParams>,
  options?: NewOptions<TData, TParams>,
  plugins?: Plugin<TData, TParams>[],
): Result<TData, TParams> => {
  const { ready, ...other } = options || {};
  const [readyValue, setReadyValue] = useState<boolean>(false);

  const checkReady = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (typeof ready === 'function') {
        const result = ready();

        if (typeof result === 'boolean') {
          return resolve(result);
        } else {
          return result.then(resolve);
        }
      }
      return resolve(!!ready);
    });
  };

  useAsyncEffect(async () => {
    const isReady = await checkReady();
    readyValue !== isReady && setReadyValue(isReady);
  }, other.refreshDeps || []);

  return useRequest<TData, TParams>(service, { ...other, ready: readyValue }, plugins);
};
